import React, { useState, useEffect } from "react";
import { supabase } from "../../lib/supabaseClient";

function Toast({ toast }) {
  if (!toast) return null;
  return (
    <div className={`fixed top-5 right-5 z-50 px-5 py-3 rounded-lg shadow-lg text-sm font-medium ${
      toast.type === "success" ? "bg-green-600 text-white" : "bg-red-600 text-white"
    }`}>
      {toast.message}
    </div>
  );
}

function GroupSection({ title, assigned, available, onToggle }) {
  if (assigned.length === 0 && available.length === 0) return null;
  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{title}</p>
      {assigned.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {assigned.map((group) => (
            <button key={group} onClick={() => onToggle(group)}
              className="flex items-center justify-between px-4 py-3 rounded-xl bg-blue-900 text-white text-sm font-medium hover:bg-blue-800 transition-colors group">
              <span className="truncate">{group}</span>
              <svg className="w-4 h-4 ml-2 flex-shrink-0 opacity-60 group-hover:opacity-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          ))}
        </div>
      )}
      {available.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {available.map((group) => (
            <button key={group} onClick={() => onToggle(group)}
              className="flex items-center justify-between px-4 py-3 rounded-xl border border-gray-200 text-gray-700 text-sm font-medium hover:border-blue-900 hover:bg-blue-50 transition-colors group">
              <span className="truncate">{group}</span>
              <svg className="w-4 h-4 ml-2 flex-shrink-0 text-gray-300 group-hover:text-blue-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function GroupManagement() {
  const [managers, setManagers] = useState([]);
  const [allLocationGroups, setAllLocationGroups] = useState([]);
  const [allRoleGroups, setAllRoleGroups] = useState([]);
  const [selectedManager, setSelectedManager] = useState(null);
  const [assignedGroups, setAssignedGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [managerSearch, setManagerSearch] = useState("");
  const [groupSearch, setGroupSearch] = useState("");
  const [toast, setToast] = useState(null);
  const [dirty, setDirty] = useState(false);

  function showToast(message, type = "success") {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }

  useEffect(() => {
    async function load() {
      setLoading(true);

      const { data: managersData, error: managersError } = await supabase.rpc("get_all_managers");
      if (managersError) {
        console.error("Managers fetch error:", managersError);
        showToast("Failed to load managers", "error");
        setLoading(false);
        return;
      }
      setManagers(managersData || []);

      const { data: groupsData, error: groupsError } = await supabase.rpc("get_all_location_groups");
      if (groupsError) {
        console.error("Groups fetch error:", groupsError);
        showToast("Failed to load groups", "error");
        setLoading(false);
        return;
      }

      const all = (groupsData || []).filter(Boolean).sort();
      // Locations start with a digit code (e.g. "201 Leichhardt")
      setAllLocationGroups(all.filter(g => /^\d/.test(g)));
      // Roles are everything else (e.g. "Branch Manager", "Projects Coordinator")
      setAllRoleGroups(all.filter(g => !/^\d/.test(g)));

      setLoading(false);
    }
    load();
  }, []);

  async function selectManager(manager) {
    if (dirty) {
      const ok = window.confirm("You have unsaved changes. Discard them?");
      if (!ok) return;
    }
    setSelectedManager(manager);
    setGroupSearch("");
    setDirty(false);
    const { data, error } = await supabase
      .from("manager_groups")
      .select("group_name")
      .eq("manager_id", manager.user_id);
    if (error) {
      console.error(error);
      showToast("Failed to load group assignments", "error");
      return;
    }
    setAssignedGroups((data || []).map(d => d.group_name));
  }

  function toggleGroup(group) {
    setAssignedGroups(prev =>
      prev.includes(group) ? prev.filter(g => g !== group) : [...prev, group]
    );
    setDirty(true);
  }

  async function saveAssignments() {
    if (!selectedManager) return;
    setSaving(true);
    const { error: deleteError } = await supabase
      .from("manager_groups")
      .delete()
      .eq("manager_id", selectedManager.user_id);
    if (deleteError) {
      showToast("Error saving — please try again", "error");
      setSaving(false);
      return;
    }
    if (assignedGroups.length > 0) {
      const rows = assignedGroups.map(group_name => ({ manager_id: selectedManager.user_id, group_name }));
      const { error: insertError } = await supabase.from("manager_groups").insert(rows);
      if (insertError) {
        showToast("Error saving — please try again", "error");
        setSaving(false);
        return;
      }
    }
    setDirty(false);
    showToast(`Saved ${assignedGroups.length} group${assignedGroups.length !== 1 ? "s" : ""} for ${selectedManager.first_name} ${selectedManager.last_name}`);
    setSaving(false);
  }

  const filteredManagers = managers.filter(m =>
    `${m.first_name} ${m.last_name} ${m.email}`.toLowerCase().includes(managerSearch.toLowerCase())
  );

  const q = groupSearch.toLowerCase();
  const filteredLocations = allLocationGroups.filter(g => g.toLowerCase().includes(q));
  const filteredRoles = allRoleGroups.filter(g => g.toLowerCase().includes(q));

  const assignedLocations = filteredLocations.filter(g => assignedGroups.includes(g));
  const availableLocations = filteredLocations.filter(g => !assignedGroups.includes(g));
  const assignedRoles = filteredRoles.filter(g => assignedGroups.includes(g));
  const availableRoles = filteredRoles.filter(g => !assignedGroups.includes(g));

  const noResults = filteredLocations.length === 0 && filteredRoles.length === 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900" />
      </div>
    );
  }

  return (
    <>
      <Toast toast={toast} />
      <div className="p-6 space-y-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Group Management</h1>
          <p className="text-sm text-gray-500 mt-1">
            Select a manager, then assign location groups and/or role groups they can see in their team dashboard.
          </p>
        </div>

        <div className="flex gap-4 h-[calc(100vh-200px)]">
          {/* LEFT: Manager list */}
          <div className="w-72 flex-shrink-0 bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Managers ({managers.length})</p>
              <input
                type="text"
                placeholder="Search managers..."
                value={managerSearch}
                onChange={e => setManagerSearch(e.target.value)}
                className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-900"
              />
            </div>
            <div className="overflow-y-auto flex-1">
              {filteredManagers.length === 0 && (
                <p className="text-center text-sm text-gray-400 py-8">No managers found</p>
              )}
              {filteredManagers.map(m => {
                const isSelected = selectedManager?.user_id === m.user_id;
                return (
                  <button
                    key={m.user_id}
                    onClick={() => selectManager(m)}
                    className={`w-full text-left px-4 py-3 border-b border-gray-50 transition-colors hover:bg-gray-50 ${isSelected ? "bg-blue-900 hover:bg-blue-900" : ""}`}
                  >
                    <p className={`text-sm font-medium truncate ${isSelected ? "text-white" : "text-gray-800"}`}>
                      {m.first_name} {m.last_name}
                    </p>
                    <p className={`text-xs truncate mt-0.5 ${isSelected ? "text-blue-200" : "text-gray-400"}`}>{m.email}</p>
                    {isSelected && (
                      <span className="inline-block mt-1 text-xs bg-white/20 text-white px-2 py-0.5 rounded-full">
                        {assignedGroups.length} group{assignedGroups.length !== 1 ? "s" : ""} assigned
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* RIGHT: Group assignment */}
          <div className="flex-1 bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col overflow-hidden">
            {!selectedManager ? (
              <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
                <svg className="w-12 h-12 mb-3 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <p className="text-sm font-medium">Select a manager to assign groups</p>
              </div>
            ) : (
              <>
                <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">
                      {selectedManager.first_name} {selectedManager.last_name}
                    </h2>
                    <p className="text-xs text-gray-400 mt-0.5">{selectedManager.email}</p>
                  </div>
                  <button
                    onClick={saveAssignments}
                    disabled={saving || !dirty}
                    className="px-5 py-2 bg-blue-900 text-white text-sm font-medium rounded-lg hover:bg-blue-800 disabled:opacity-40 transition-colors"
                  >
                    {saving ? "Saving..." : dirty ? "Save Changes" : "Saved"}
                  </button>
                </div>

                <div className="px-5 py-3 border-b border-gray-100">
                  <input
                    type="text"
                    placeholder="Search groups..."
                    value={groupSearch}
                    onChange={e => setGroupSearch(e.target.value)}
                    className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-900"
                  />
                </div>

                <div className="flex-1 overflow-y-auto p-5 space-y-8">
                  <GroupSection
                    title={`Locations (${assignedLocations.length} assigned)`}
                    assigned={assignedLocations}
                    available={availableLocations}
                    onToggle={toggleGroup}
                  />
                  <GroupSection
                    title={`Roles (${assignedRoles.length} assigned)`}
                    assigned={assignedRoles}
                    available={availableRoles}
                    onToggle={toggleGroup}
                  />
                  {noResults && (
                    <p className="text-center text-sm text-gray-400 py-8">No groups match your search</p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
